import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CreateAnnexIVDraftResponse,
  DocumentationParams,
  DocumentResponse
} from '../../types/Documentation.types';
import { CreateEUDeclarationResponse } from '../../types/EUDeclaration.types';
import { CreateFRIAResponse } from '../../types/FRIA.types';
import { FieldValue } from '../../types/DocumentationFieldValue';
import { StepStatus } from '../../types/RequirementSteps.types';
import { CreateDPIAResponse } from '../../types/DPIA.types';
type DocumentationState = {
  documentationReqBody: DocumentationParams;
  createAnnexIVDraftResponse: CreateAnnexIVDraftResponse | null;
  createEUDeclarationResponse: CreateEUDeclarationResponse | null;
  createFRIAResponse: CreateFRIAResponse | null;
  createDPIAResponse: CreateDPIAResponse | null;
  annexIVFields: Record<string, FieldValue>;
  euDeclarationFields: Record<string, FieldValue>;
  friaFields: Record<string, FieldValue>;
  dpiaFields: Record<string, FieldValue>;
  documents: DocumentResponse[];
  documentsTotal: number;
  documentsLoading: boolean;
  previewDocument: DocumentResponse | null;
  euDeclarationStatus: StepStatus;
  friaStatus: 'draft' | 'complete' | null;
  order: 'asc' | 'desc' | undefined;
};

export const initialDocumentationParams: DocumentationParams = {
  aiSystemId: '', // Will be set from URL params
  source: [],
  category: [],
  createdFrom: null,
  createdTo: null,
  keyword: null,
  limit: 10,
  offset: 0,
  sortBy: 'createdAt',
  order: 'desc'
};

const initialState: DocumentationState = {
  documentationReqBody: initialDocumentationParams,
  createAnnexIVDraftResponse: null,
  createEUDeclarationResponse: null,
  createFRIAResponse: null,
  createDPIAResponse: null,
  annexIVFields: {},
  euDeclarationFields: {},
  friaFields: {},
  dpiaFields: {},
  documents: [],
  documentsTotal: 0,
  documentsLoading: false,
  previewDocument: null,
  euDeclarationStatus: 'NOT_STARTED',
  friaStatus: 'draft',
  order: 'desc'
};

export const documentationSlice = createSlice({
  name: 'documentation',
  initialState,
  reducers: {
    setPreviewDocument: (
      state,
      action: PayloadAction<DocumentResponse | null>
    ) => {
      state.previewDocument = action.payload;
    },
    setDocumentationReqBody: (
      state,
      action: PayloadAction<DocumentationParams>
    ) => {
      state.documentationReqBody = action.payload;
    },
    resetDocumentationReqBody: (state) => {
      state.documentationReqBody = {
        ...initialState.documentationReqBody,
        aiSystemId: state.documentationReqBody.aiSystemId
      };
    },
    setCreateAnnexIVDraftResponse: (
      state,
      action: PayloadAction<CreateAnnexIVDraftResponse>
    ) => {
      state.createAnnexIVDraftResponse = action.payload;
    },
    setCreateEUDeclarationResponse: (
      state,
      action: PayloadAction<CreateEUDeclarationResponse>
    ) => {
      state.createEUDeclarationResponse = action.payload;
    },
    setAnnexIVField: (
      state,
      action: PayloadAction<{ label: string; value: string; section: string }>
    ) => {
      const { label, value, section } = action.payload;
      state.annexIVFields[label] = { label, value, section };
    },
    setAnnexIVFieldsState: (
      state,
      action: PayloadAction<Record<string, FieldValue>>
    ) => {
      state.annexIVFields = action.payload;
    },
    resetAnnexIVFields: (state) => {
      state.annexIVFields = {};
    },
    setEUDeclarationField: (
      state,
      action: PayloadAction<{ label: string; value: string; section: string }>
    ) => {
      const { label, value, section } = action.payload;
      state.euDeclarationFields[label] = { label, value, section };
    },
    setEUDeclarationFieldsState: (
      state,
      action: PayloadAction<Record<string, FieldValue>>
    ) => {
      state.euDeclarationFields = action.payload;
    },
    resetEUDeclarationFields: (state) => {
      state.euDeclarationFields = {};
    },
    setCreateFRIAResponse: (
      state,
      action: PayloadAction<CreateFRIAResponse>
    ) => {
      state.createFRIAResponse = action.payload;
    },

    setFRIAField: (
      state,
      action: PayloadAction<{ label: string; value: string; section: string }>
    ) => {
      const { label, value, section } = action.payload;
      state.friaFields[label] = { label, value, section };
    },
    setFRIAFieldsState: (
      state,
      action: PayloadAction<Record<string, FieldValue>>
    ) => {
      state.friaFields = action.payload;
    },
    resetFRIAFields: (state) => {
      state.friaFields = {};
    },
    setFRIAStatus: (
      state,
      action: PayloadAction<'draft' | 'complete' | null>
    ) => {
      state.friaStatus = action.payload;
    },
    setCreateDPIAResponse: (
      state,
      action: PayloadAction<CreateDPIAResponse>,
    ) => {
      state.createDPIAResponse = action.payload;
    },
    setDPIAField: (
      state,
      action: PayloadAction<{ label: string; value: string; section: string }>,
    ) => {
      const { label, value, section } = action.payload;
      state.dpiaFields[label] = { label, value, section };
    },
    setDPIAFieldsState: (
      state,
      action: PayloadAction<Record<string, FieldValue>>,
    ) => {
      state.dpiaFields = action.payload;
    },
    resetDPIAFields: (state) => {
      state.dpiaFields = {};
    },
    setDocuments: (
      state,
      action: PayloadAction<{ items: DocumentResponse[]; total: number }>
    ) => {
      state.documents = action.payload.items;
      state.documentsTotal = action.payload.total;
      state.documentsLoading = false;
    },
    setDocumentsLoading: (state, action: PayloadAction<boolean>) => {
      state.documentsLoading = action.payload;
    },
    setEUDeclarationStatus: (state, action: PayloadAction<StepStatus>) => {
      state.euDeclarationStatus = action.payload;
    }
  }
});

export const {
  setPreviewDocument,
  setDocumentationReqBody,
  resetDocumentationReqBody,
  setCreateAnnexIVDraftResponse,
  setCreateEUDeclarationResponse,
  setCreateFRIAResponse,
  setAnnexIVField,
  setAnnexIVFieldsState,
  resetAnnexIVFields,
  setEUDeclarationField,
  setEUDeclarationFieldsState,
  resetEUDeclarationFields,
  setFRIAField,
  setFRIAFieldsState,
  resetFRIAFields,
  setFRIAStatus,
  setCreateDPIAResponse,
  setDPIAField,
  setDPIAFieldsState,
  resetDPIAFields,
  setDocuments,
  setDocumentsLoading,
  setEUDeclarationStatus
} = documentationSlice.actions;

export default documentationSlice.reducer;
